// Merge logic for the shared LACW "Aid Pending" queue. The same queue also holds
// "To be claimed" and "Counsel appeared" (Awaiting fee slip) appearances; each item's
// `action` says which list it shows on.
//
// The queue lives in a single vals_drafts row that several staff edit at once, so the
// interesting behaviour is how two concurrent edits are reconciled. That is pure data
// handling with no React in it, which is why it lives here rather than in the page - it
// can then be unit tested directly instead of asserted against page source text.

// Matters are identified by their natural key rather than a generated id, because the same
// appearance can be re-parsed from a fresh diary upload and must not duplicate.
export const pendingAidKey = (entry = {}) => [
  entry.date,
  entry.client_name,
  entry.court,
  entry.appearance_type,
  entry.lawyer_initials,
].map(value => String(value || "").trim().toLowerCase()).join("|");

// Plain union. Correct when combining sources that only ever add - a fresh diary parse, a
// recovered draft, a restored archive. NOT correct for resolving a save conflict; use
// threeWayMergePendingAid for that.
export const mergePendingAidItems = (...lists) => {
  const merged = new Map();
  lists.flat().filter(Boolean).forEach(item => {
    const key = item.pendingKey || pendingAidKey(item);
    if (!key) return;
    const existing = merged.get(key);
    merged.set(key, { ...existing, ...item, pendingKey: key, action: item.action || existing?.action || "Aid pending" });
  });
  return Array.from(merged.values());
};

const indexPendingAidByKey = (items) => new Map(
  (items || []).filter(Boolean).map(item => {
    const key = item.pendingKey || pendingAidKey(item);
    return [key, { ...item, pendingKey: key }];
  })
);

// Field-order-independent comparison. Values here are strings and ISO dates.
const samePendingAidItem = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if (String(a[key] ?? "") !== String(b[key] ?? "")) return false;
  }
  return true;
};

export const samePendingAidList = (a, b) => {
  const left = indexPendingAidByKey(a);
  const right = indexPendingAidByKey(b);
  if (left.size !== right.size) return false;
  for (const [key, item] of left) {
    if (!right.has(key) || !samePendingAidItem(item, right.get(key))) return false;
  }
  return true;
};

// Three-way merge used when the server rejects a save with 409 because someone else wrote
// first.
//
// A union would be wrong here: matters are *removed* from this queue once they are actioned
// (aid granted, matter withdrawn), so unioning our copy with theirs would resurrect matters
// a colleague just cleared - silently undoing their work.
//
// Because the page keeps the queue as it stood at its last successful save, there is a
// common ancestor, which is what distinguishes "they added this" from "we deleted this":
//   - start from `theirs`, which already holds everything they added or kept
//   - drop what we removed, unless they edited it after the ancestor
//   - re-apply what we added or edited
//
// An edit beats a concurrent delete: losing a note someone typed is worse than leaving an
// extra row that can simply be actioned again.
export const threeWayMergePendingAid = (base, mine, theirs) => {
  const baseMap = indexPendingAidByKey(base);
  const mineMap = indexPendingAidByKey(mine);
  const theirsMap = indexPendingAidByKey(theirs);
  const merged = new Map(theirsMap);

  baseMap.forEach((baseItem, key) => {
    if (mineMap.has(key)) return; // we still hold it; not a deletion
    const theirItem = theirsMap.get(key);
    if (theirItem && !samePendingAidItem(theirItem, baseItem)) return; // they edited it
    merged.delete(key); // we deleted it and they did not touch it
  });

  mineMap.forEach((mineItem, key) => {
    const baseItem = baseMap.get(key);
    if (!baseItem) {
      merged.set(key, mineItem); // we added it
      return;
    }
    if (!samePendingAidItem(mineItem, baseItem)) {
      merged.set(key, { ...merged.get(key), ...mineItem }); // we edited it
    }
  });

  return Array.from(merged.values());
};
